import { PlusCircle, UsersThree } from "@phosphor-icons/react";
import clsx from "clsx";

import RenderIf from "common/components/RenderIf";
import type { ButtonVariantType } from "common/components/ui/Button";
import { Tag } from "common/components/ui/Tag";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useValidateFlow from "../../../../../hooks/useValidateFlow";
import FlowActions from "../FlowActions";

import type { CampaignFlow } from "../../../../../types";

interface SelectionButtonProps {
  targetCount: number;
  flow: CampaignFlow;
  isSelected: boolean;
  onClick?: () => void;
  deleteFlow?: (flowId: string) => void;
}

export default function SelectionButton({
  targetCount,
  flow,
  isSelected,
  onClick = null,
  deleteFlow = null,
}: SelectionButtonProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { isFlowError } = useValidateFlow(flow.id);

  let tabStyles = "bg-black-100 hover:bg-black-200";
  let buttonVariant: ButtonVariantType = "ghost-black";
  if (isFlowError) {
    tabStyles = "border border-red-500 bg-red-50 text-red-500";
    buttonVariant = "ghost-danger";
  } else if (isSelected) {
    tabStyles = getWhitelabelCustomization()?.primary_color
      ? "bg-whitelabel-primary text-whitelabel-contrast"
      : "bg-black-950 text-white";
    buttonVariant = "default-black";
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: this element has a button as a child, it can't be a button
    <div
      role="button"
      tabIndex={0}
      className={clsx(
        "flex h-10 w-full items-center rounded-2xl pl-4 pr-1.5 text-button-sm lg:h-12 lg:text-button-md transition-colors duration-300 ease-in-out",
        tabStyles,
      )}
      onClick={() => {
        if (onClick) {
          onClick();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && onClick) {
          onClick();
        }
      }}
    >
      <RenderIf condition={!flow}>
        <PlusCircle size={20} weight="fill" className="mr-2.5" />
      </RenderIf>

      <span className="line-clamp-1">
        {flow ? flow.name : "Create split test"}
      </span>

      <Tag
        leftIcon={isDesktop && <UsersThree weight="fill" />}
        className="ml-3"
        variant={
          getWhitelabelCustomization()?.primary_color ? "tertiary" : "primary"
        }
      >
        {targetCount}
      </Tag>

      <RenderIf condition={isDesktop}>
        <FlowActions
          flow={flow}
          variant={buttonVariant}
          deleteFlow={deleteFlow}
        />
      </RenderIf>
    </div>
  );
}
